import React from 'react';

/**
 * Parsea y formatea texto según las reglas de formato de WhatsApp:
 * - *negrita* => <strong>
 * - _cursiva_ => <em>
 * - ~tachado~ => <del>
 * - ```bloque de código``` => <pre><code>
 * - `código en línea` => <code>
 * - URLs => <a>
 * - Saltos de línea y emojis preservados de forma nativa
 * 
 * @param {string} text - Texto plano con formato de WhatsApp
 * @returns {React.ReactNode}
 */
export const formatWhatsAppText = (text, options = {}) => {
  if (!text || typeof text !== 'string') return text;

  // 1. Separar bloques de código (```...```) primero para no formatear su interior
  const codeBlockRegex = /```([\s\S]*?)```/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
    }
    parts.push({ type: 'codeblock', content: match[1] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.substring(lastIndex) });
  }

  // 2. Parser recursivo para formatos en línea (URLs, código, negrita, cursiva, tachado)
  const parseInline = (str, keyPrefix = 'wa') => {
    if (!str) return [];

    // Regex para tokens en línea
    const inlineRegex = /(https?:\/\/[^\s<]+)|(`[^`\r\n]+?`)|(\*(?!\s)[^*\r\n]+?(?<!\s)\*)|(_(?!\s)[^_\r\n]+?(?<!\s)_)|(~(?!\s)[^~\r\n]+?(?<!\s)~)/g;

    const elements = [];
    let curIdx = 0;
    let m;

    while ((m = inlineRegex.exec(str)) !== null) {
      if (m.index > curIdx) {
        elements.push(str.substring(curIdx, m.index));
      }

      const matchStr = m[0];
      const key = `${keyPrefix}-${m.index}`;

      if (m[1]) {
        // Enlace URL
        elements.push(
          <a
            key={key}
            href={matchStr}
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-underline"
            style={{ color: 'inherit', fontWeight: 500 }}
          >
            {matchStr}
          </a>
        );
      } else if (m[2]) {
        // Código en línea `...`
        elements.push(
          <code
            key={key}
            className="px-1 py-0.5 rounded font-monospace"
            style={{
              backgroundColor: 'rgba(0,0,0,0.08)',
              fontSize: '0.85em'
            }}
          >
            {matchStr.slice(1, -1)}
          </code>
        );
      } else if (m[3]) {
        // Negrita *...*
        const inner = matchStr.slice(1, -1);
        elements.push(
          <strong key={key} style={{ fontWeight: 700 }}>
            {parseInline(inner, `${key}-b`)}
          </strong>
        );
      } else if (m[4]) {
        // Cursiva _..._
        const inner = matchStr.slice(1, -1);
        elements.push(
          <em key={key} style={{ fontStyle: 'italic' }}>
            {parseInline(inner, `${key}-i`)}
          </em>
        );
      } else if (m[5]) {
        // Tachado ~...~
        const inner = matchStr.slice(1, -1);
        elements.push(
          <del key={key} style={{ textDecoration: 'line-through' }}>
            {parseInline(inner, `${key}-s`)}
          </del>
        );
      }

      curIdx = m.index + matchStr.length;
    }

    if (curIdx < str.length) {
      elements.push(str.substring(curIdx));
    }

    return elements;
  };

  return parts.map((part, i) => {
    if (part.type === 'codeblock') {
      if (options.inlineOnly) {
        return (
          <code
            key={`cb-${i}`}
            className="px-1 py-0.5 rounded font-monospace"
            style={{
              backgroundColor: 'rgba(0,0,0,0.08)',
              fontSize: '0.85em'
            }}
          >
            {part.content}
          </code>
        );
      }
      return (
        <pre
          key={`cb-${i}`}
          className="p-2 rounded font-monospace my-1"
          style={{
            backgroundColor: 'rgba(0,0,0,0.10)',
            fontSize: '0.82rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: '4px 0'
          }}
        >
          <code>{part.content}</code>
        </pre>
      );
    }
    return <React.Fragment key={`p-${i}`}>{parseInline(part.content, `part-${i}`)}</React.Fragment>;
  });
};
